import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Col,
  Descriptions,
  message,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  LineChartOutlined,
  PrinterOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { useProjectStatusCheck } from '@/hooks/useProjects';
import { useDocumentBreadcrumb } from '@/contexts/PageBreadcrumbContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import type { ProjectStatusCheckResponse } from '@/services/projectStatusCheck.service';
import { ControlTowerMetricStrip } from '@/components/control-tower/ControlTowerMetricStrip';
import { ControlTowerMetricTile } from '@/components/control-tower/ControlTowerMetricTile';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { ControlTowerSubQueuePanel } from '@/components/control-tower/ControlTowerSubQueuePanel';
import EstimationPerformancePanel from '@/components/sales/EstimationPerformancePanel';
import { openEngineCommercialPrint } from '@/components/print/openEngineCommercialPrint';
import { projectService } from '@/services/project.service';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

const SHARED_INVOICE_SHARE_HINT =
  "This invoice covers multiple orders/projects. Amounts shown are this order/project's share.";

const ProjectStatusCheckPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const { data, isLoading, isError, error } = useProjectStatusCheck(id ?? '');

  const breadcrumbSegments = useMemo(
    () =>
      data?.project
        ? [
            { title: 'Projects', path: '/prj/projects' },
            {
              title: data.project.project_number || data.project.project_name,
              path: `/prj/projects/${data.project.id}/edit`,
            },
            { title: 'Project health' },
          ]
        : null,
    [data?.project]
  );
  useDocumentBreadcrumb(breadcrumbSegments);

  const stages = useMemo(() => {
    if (!data) return [];
    const p = data.project;
    const arOpen = data.commercial_rollup.ar_outstanding > 0;
    const qualityIssue = data.quality.critical_ncr_count > 0;
    return [
      {
        title: 'Planning',
        status:
          p.project_status === 'Planning'
            ? 'process'
            : p.project_status === 'Cancelled'
              ? 'error'
              : 'finish',
      },
      {
        title: 'Commercial',
        description: `${data.linked_sales_orders.length} SO(s)`,
        status: data.linked_sales_orders.length > 0 ? 'finish' : 'wait',
      },
      {
        title: 'Production',
        description: `${data.work_orders.length} WO(s)`,
        status: data.work_orders.length > 0 ? 'finish' : 'wait',
      },
      {
        title: 'Delivery',
        description: `${data.deliveries.length} DN(s)`,
        status: data.deliveries.length > 0 ? 'finish' : 'wait',
      },
      {
        title: 'Billing',
        description: `${data.invoices.length} invoice(s)`,
        status: arOpen ? 'process' : data.invoices.length > 0 ? 'finish' : 'wait',
      },
      {
        title: 'Job costing',
        description: `${formatCurrency(data.job_costing.posted_cost_total)} posted`,
        status:
          data.job_costing.posted_cost_total > data.job_costing.budget_total
            ? 'error'
            : data.job_costing.posted_cost_total > 0
              ? 'finish'
              : 'wait',
      },
      {
        title: 'Quality',
        description: `${data.quality.open_ncr_count} NCR(s)`,
        status: qualityIssue ? 'error' : data.quality.open_ncr_count > 0 ? 'process' : 'finish',
      },
    ] as const;
  }, [data, formatCurrency]);

  const healthTag = useMemo(() => {
    switch (data?.health) {
      case 'Healthy':
        return <Tag color="success">Healthy</Tag>;
      case 'Financial risk':
        return <Tag color="error">Financial risk</Tag>;
      case 'Attention needed':
        return <Tag color="warning">Attention needed</Tag>;
      case 'Cancelled':
        return <Tag>Cancelled</Tag>;
      default:
        return null;
    }
  }, [data?.health]);

  type LinkedSoRow = ProjectStatusCheckResponse['linked_sales_orders'][number];
  type WorkOrderRow = ProjectStatusCheckResponse['work_orders'][number];
  type InvoiceRow = ProjectStatusCheckResponse['invoices'][number];
  type ProcurementPoRow = ProjectStatusCheckResponse['procurement']['pos'][number];

  const soColumns: ColumnsType<LinkedSoRow> = [
    { title: 'SO #', dataIndex: 'sales_order_number', key: 'sales_order_number' },
    { title: 'Customer', dataIndex: 'customer_name', key: 'customer_name' },
    { title: 'Status', dataIndex: 'order_status', key: 'order_status', render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Grand total',
      dataIndex: 'grand_total',
      key: 'grand_total',
      align: 'right',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Signal',
      key: 'signal',
      render: (_, r) => (
        <Tag color={r.order_health_signal.level === 'red' ? 'red' : r.order_health_signal.level === 'amber' ? 'gold' : 'green'}>
          {r.order_health_signal.summary}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
          Open SO health
        </Button>
      ),
    },
  ];

  const workOrderColumns: ColumnsType<WorkOrderRow> = [
    { title: 'WO #', dataIndex: 'work_order_number', key: 'work_order_number' },
    { title: 'Status', dataIndex: 'work_order_status', key: 'work_order_status', render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Order date',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
          View
        </Button>
      ),
    },
  ];

  const invoiceColumns: ColumnsType<InvoiceRow> = [
    {
      title: 'Invoice #',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (v, r) => (
        <Space size={6} wrap>
          <span>{v}</span>
          {r.is_shared ? (
            <Tooltip title={SHARED_INVOICE_SHARE_HINT}>
              <Tag color="blue">Shared</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    { title: 'Type', dataIndex: 'invoice_type', key: 'invoice_type' },
    { title: 'Status', dataIndex: 'invoice_status', key: 'invoice_status', render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Invoice total',
      dataIndex: 'grand_total',
      key: 'grand_total',
      align: 'right',
      render: (v, r) => (
        <Tooltip title={r.is_shared ? `Full invoice ${formatCurrency(v)}` : undefined}>
          <span>{formatCurrency(v)}</span>
        </Tooltip>
      ),
    },
    {
      title: 'This project',
      key: 'share',
      align: 'right',
      render: (_, r) => formatCurrency(r.attributed_billed ?? (r.invoice_type === 'Advance' ? 0 : r.grand_total)),
    },
    {
      title: 'Balance due',
      key: 'balance_due',
      align: 'right',
      render: (_, r) => {
        const ar = r.attributed_ar ?? r.balance_due;
        return <Text type={Number(ar) > 0 ? 'danger' : undefined}>{formatCurrency(ar)}</Text>;
      },
    },
  ];

  const procurementColumns: ColumnsType<ProcurementPoRow> = [
    { title: 'PO #', dataIndex: 'po_number', key: 'po_number', width: 130 },
    {
      title: 'Date',
      dataIndex: 'po_date',
      key: 'po_date',
      width: 110,
      render: (v) => (v ? dayjs(v).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'po_status',
      key: 'po_status',
      width: 120,
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: 'Amount',
      dataIndex: 'grand_total',
      key: 'grand_total',
      align: 'right',
      width: 130,
      render: (v, row) => formatCurrency(v, row.currency),
    },
    {
      title: 'Action',
      key: 'action',
      width: 80,
      render: (_, r) => (
        <Button type="link" size="small" onClick={() => navigate(r.link_path)}>
          View
        </Button>
      ),
    },
  ];

  const boqColumns: ColumnsType<ProjectStatusCheckResponse['estimation']['boqs'][number]> = [
    { title: 'BOQ #', dataIndex: 'boq_number', key: 'boq_number' },
    { title: 'Name', dataIndex: 'boq_name', key: 'boq_name' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      align: 'right',
      render: (v) => formatCurrency(v),
    },
  ];

  const treColumns: ColumnsType<ProjectStatusCheckResponse['estimation']['trading_rental_estimations'][number]> = [
    { title: 'TRE #', dataIndex: 'estimation_number', key: 'estimation_number' },
    { title: 'Type', dataIndex: 'estimation_type', key: 'estimation_type' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (v) => <Tag>{v}</Tag> },
    {
      title: 'Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      align: 'right',
      render: (v) => formatCurrency(v),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Alert
        type="error"
        message={(error as Error)?.message || 'Failed to load project health check'}
      />
    );
  }

  const handlePrintFinancialPosition = async () => {
    try {
      const fp = (await projectService.getFinancialPosition(data.project.id)) as any;
      const cats = Array.isArray(fp?.by_category) ? fp.by_category : [];
      openEngineCommercialPrint({
        title: 'Project financial position',
        documentNumber: data.project.project_number,
        partyName: data.project.customer_name,
        docType: 'project_financial_position',
        metaLines: [
          { label: 'Project', value: data.project.project_name },
          { label: 'EAC', value: formatMoney(Number(fp?.totals?.eac || 0)) },
          {
            label: 'Billed margin',
            value: formatMoney(Number(fp?.margins?.billed?.profit || 0)),
          },
          {
            label: 'Accrued Cost liability',
            value: formatMoney(Number(fp?.accrued_unbilled_cost?.liability_balance || 0)),
          },
          {
            label: 'Overrun',
            value: (fp?.overrun?.flags || []).join('; ') || 'None',
          },
        ],
        columns: [
          { key: 'cat', label: 'Category' },
          { key: 'budget', label: 'Budget', align: 'right' },
          { key: 'commit', label: 'Committed', align: 'right' },
          { key: 'actual', label: 'Actual', align: 'right' },
          { key: 'avail', label: 'Available', align: 'right' },
        ],
        lines: cats.map((c: any, i: number) => ({
          id: String(i),
          lineNumber: i + 1,
          description: String(c.cost_category || ''),
          cells: {
            cat: String(c.cost_category || ''),
            budget: formatMoney(Number(c.budgeted || 0)),
            commit: formatMoney(Number(c.committed || 0)),
            actual: formatMoney(Number(c.actual || 0)),
            avail: formatMoney(Number(c.available || 0)),
          },
        })),
      });
    } catch (e: any) {
      message.error(e?.message || 'Failed to load financial position for print');
    }
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Project health check"
          documentNumber={data.project.project_number}
          statusTag={healthTag}
          subtitle={
            <span>
              {data.project.project_name} · {data.project.customer_name}
            </span>
          }
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/prj/projects')}>
                Back to projects
              </Button>
              <Button icon={<PrinterOutlined />} onClick={handlePrintFinancialPosition}>
                Print P&amp;L
              </Button>
              <Button icon={<LineChartOutlined />} onClick={() => navigate('/prj/project-health')}>
                Health hub
              </Button>
              <Button icon={<FileTextOutlined />} onClick={() => navigate(`/prj/projects/${data.project.id}/edit`)}>
                Edit project
              </Button>
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={stages as any} />}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {data.blockers.length > 0 && (
          <Alert
            type={data.health === 'Financial risk' ? 'error' : 'warning'}
            showIcon
            message="Blockers"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {data.blockers.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>
            }
          />
        )}

        <ControlTowerSectionCard zone="actions" title="Overview">
          <Descriptions column={4} size="small">
            <Descriptions.Item label="Project">{data.project.project_name}</Descriptions.Item>
            <Descriptions.Item label="Status">{data.project.project_status}</Descriptions.Item>
            <Descriptions.Item label="Completion">
              {data.project.completion_percentage.toFixed(1)}%
            </Descriptions.Item>
            <Descriptions.Item label="Planned end">
              {data.project.planned_end_date ? dayjs(data.project.planned_end_date).format('DD MMM YYYY') : '—'}
            </Descriptions.Item>
          </Descriptions>
        </ControlTowerSectionCard>

        <ControlTowerMetricStrip zone="metricsOrders" title="Commercial roll-up">
          <Col xs={24} sm={12} md={6}>
            {data.profitability.forecast_basis === 'estimate' ? (
              <ControlTowerMetricTile
                title="Estimated value"
                count={Math.round(data.profitability.forecast.revenue)}
                valueDisplay={formatCurrency(data.profitability.forecast.revenue)}
                urgency="neutral"
                extra={
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                    Est. cost {formatCurrency(data.estimation.estimated_budget_total)} · from BOQ / TRE
                    until a customer order is booked
                  </Text>
                }
              />
            ) : (
              <ControlTowerMetricTile
                title="Contract value"
                count={Math.round(data.commercial_rollup.contract_value)}
                valueDisplay={formatCurrency(data.commercial_rollup.contract_value)}
                urgency="neutral"
              />
            )}
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="Ordered total"
              count={Math.round(data.commercial_rollup.ordered_total)}
              valueDisplay={formatCurrency(data.commercial_rollup.ordered_total)}
              urgency="neutral"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="Invoiced total"
              count={Math.round(data.commercial_rollup.invoiced_total)}
              valueDisplay={formatCurrency(data.commercial_rollup.invoiced_total)}
              urgency="neutral"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="AR outstanding"
              count={Math.round(data.commercial_rollup.ar_outstanding)}
              valueDisplay={formatCurrency(data.commercial_rollup.ar_outstanding)}
              urgency={data.commercial_rollup.ar_outstanding > 0 ? 'critical' : 'neutral'}
            />
          </Col>
        </ControlTowerMetricStrip>

        <ControlTowerMetricStrip zone="metricsCollections" title="Committed cost (procurement)">
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="PO ordered"
              count={Math.round(data.procurement.cost_rollup?.po_ordered ?? data.profitability.committed_cost)}
              valueDisplay={formatCurrency(
                data.procurement.cost_rollup?.po_ordered ?? data.profitability.committed_cost
              )}
              urgency="neutral"
              extra={
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  Committed cost {formatCurrency(data.profitability.committed_cost)}
                </Text>
              }
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="GRN received"
              count={Math.round(data.procurement.cost_rollup?.grn_received ?? 0)}
              valueDisplay={formatCurrency(data.procurement.cost_rollup?.grn_received ?? 0)}
              urgency="neutral"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="Supplier invoiced"
              count={Math.round(data.procurement.cost_rollup?.supplier_invoiced ?? 0)}
              valueDisplay={formatCurrency(data.procurement.cost_rollup?.supplier_invoiced ?? 0)}
              urgency="neutral"
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <ControlTowerMetricTile
              title="Supplier paid"
              count={Math.round(data.procurement.cost_rollup?.supplier_paid ?? 0)}
              valueDisplay={formatCurrency(data.procurement.cost_rollup?.supplier_paid ?? 0)}
              urgency="neutral"
            />
          </Col>
        </ControlTowerMetricStrip>

        {(data.commercial_rollup.epc_contract_value != null ||
          data.commercial_rollup.epc_certified_to_date != null) && (
          <ControlTowerMetricStrip zone="metricsOrders" title="EPC commercial (Contract Admin)">
            <Col xs={24} sm={8}>
              <ControlTowerMetricTile
                title="EPC contract value"
                count={Math.round(Number(data.commercial_rollup.epc_contract_value || 0))}
                valueDisplay={formatCurrency(Number(data.commercial_rollup.epc_contract_value || 0))}
                urgency="neutral"
              />
            </Col>
            <Col xs={24} sm={8}>
              <ControlTowerMetricTile
                title="Certified to date"
                count={Math.round(Number(data.commercial_rollup.epc_certified_to_date || 0))}
                valueDisplay={formatCurrency(Number(data.commercial_rollup.epc_certified_to_date || 0))}
                urgency="neutral"
              />
            </Col>
            <Col xs={24} sm={8}>
              <ControlTowerMetricTile
                title="Retention held"
                count={Math.round(Number(data.commercial_rollup.epc_retention_held || 0))}
                valueDisplay={formatCurrency(Number(data.commercial_rollup.epc_retention_held || 0))}
                urgency="neutral"
              />
            </Col>
          </ControlTowerMetricStrip>
        )}

        {data.financial_position ? (
          <ControlTowerMetricStrip
            zone="metricsOrders"
            title={`Accrued Cost / Unbilled Cost Liability${
              data.financial_position.accrued_unbilled_cost?.account_code
                ? ` (${data.financial_position.accrued_unbilled_cost.account_code})`
                : ''
            }`}
          >
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Cost liability (outstanding)"
                count={Math.round(
                  Math.abs(
                    Number(data.financial_position.accrued_unbilled_cost?.liability_balance || 0)
                  )
                )}
                valueDisplay={formatCurrency(
                  Number(data.financial_position.accrued_unbilled_cost?.liability_balance || 0)
                )}
                urgency={
                  Number(data.financial_position.accrued_unbilled_cost?.liability_balance || 0) > 0
                    ? 'attention'
                    : Number(data.financial_position.accrued_unbilled_cost?.liability_balance || 0) < 0
                      ? 'critical'
                      : 'neutral'
                }
                extra={
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                    {data.financial_position.accrued_unbilled_cost?.account_id
                      ? 'Credit − debit on Accrued Cost clearing for this project. Positive = unreleased accrued liability (DN COGS not yet matched by MI/GRN).'
                      : 'Accrued Cost account (2170) could not be resolved for this company.'}
                  </Text>
                }
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Accrued credits (DN COGS)"
                count={Math.round(
                  Number(data.financial_position.accrued_unbilled_cost?.credit_total || 0)
                )}
                valueDisplay={formatCurrency(
                  Number(data.financial_position.accrued_unbilled_cost?.credit_total || 0)
                )}
                urgency="neutral"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Accrued debits (MI / GRN)"
                count={Math.round(
                  Number(data.financial_position.accrued_unbilled_cost?.debit_total || 0)
                )}
                valueDisplay={formatCurrency(
                  Number(data.financial_position.accrued_unbilled_cost?.debit_total || 0)
                )}
                urgency="neutral"
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <ControlTowerMetricTile
                title="Net debit balance"
                count={Math.round(
                  Math.abs(
                    Number(data.financial_position.accrued_unbilled_cost?.net_debit_balance || 0)
                  )
                )}
                valueDisplay={formatCurrency(
                  Number(data.financial_position.accrued_unbilled_cost?.net_debit_balance || 0)
                )}
                urgency="neutral"
                extra={
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                    {data.financial_position.accrued_unbilled_cost?.account_name ||
                      'Accrued Cost / Unbilled Cost Liability'}
                  </Text>
                }
              />
            </Col>
          </ControlTowerMetricStrip>
        ) : null}

        {data.financial_position?.overrun_flags?.length ? (
          <Alert
            type="error"
            showIcon
            message="Cost Control / margin overrun"
            description={
              <Space direction="vertical" size={4}>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {data.financial_position.overrun_flags.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
                <Button
                  size="small"
                  type="link"
                  onClick={() =>
                    navigate(
                      data.financial_position?.drill?.bva ||
                        `/prj/projects/${data.project.id}/budgets/variance`
                    )
                  }
                >
                  Open Budget vs Actual
                </Button>
              </Space>
            }
          />
        ) : null}

        <ControlTowerMetricStrip zone="metricsCollections" title="Profitability (revenue − cost)">
          <Col xs={24} md={8}>
            <ControlTowerMetricTile
              title={
                data.profitability.forecast_basis === 'estimate'
                  ? 'Forecast P/L (estimate)'
                  : 'Forecast P/L'
              }
              count={Math.round(data.profitability.forecast.profit)}
              valueDisplay={formatCurrency(data.profitability.forecast.profit)}
              urgency={data.profitability.forecast.profit >= 0 ? 'neutral' : 'critical'}
              extra={
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  {data.profitability.forecast_basis === 'estimate' ? (
                    <>
                      Est. selling {formatCurrency(data.profitability.forecast.revenue)} − est. cost{' '}
                      {formatCurrency(data.profitability.forecast.cost)} (
                      {data.profitability.forecast.margin_pct}%) · no customer order booked yet
                    </>
                  ) : (
                    <>
                      Revenue {formatCurrency(data.profitability.forecast.revenue)} − EAC{' '}
                      {formatCurrency(data.profitability.forecast.cost)} (
                      {data.profitability.forecast.margin_pct}%)
                      {data.financial_position?.eac != null
                        ? ` · EAC ${formatCurrency(data.financial_position.eac)}`
                        : ''}
                    </>
                  )}
                </Text>
              }
            />
          </Col>
          <Col xs={24} md={8}>
            <ControlTowerMetricTile
              title="Billed P/L"
              count={Math.round(data.profitability.billed.profit)}
              valueDisplay={formatCurrency(data.profitability.billed.profit)}
              urgency={data.profitability.billed.profit >= 0 ? 'neutral' : 'critical'}
              extra={
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  Invoiced {formatCurrency(data.profitability.billed.revenue)} − reconciled{' '}
                  {formatCurrency(data.profitability.billed.cost)} ({data.profitability.billed.margin_pct}%)
                </Text>
              }
            />
          </Col>
          <Col xs={24} md={8}>
            <ControlTowerMetricTile
              title="Cash P/L"
              count={Math.round(data.profitability.cash.profit)}
              valueDisplay={formatCurrency(data.profitability.cash.profit)}
              urgency={data.profitability.cash.profit >= 0 ? 'neutral' : 'critical'}
              extra={
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  Collected {formatCurrency(data.profitability.cash.revenue)} − reconciled{' '}
                  {formatCurrency(data.profitability.cash.cost)} ({data.profitability.cash.margin_pct}%)
                </Text>
              }
            />
          </Col>
        </ControlTowerMetricStrip>

        <ControlTowerSectionCard zone="ordersInProgress" title={`Linked Customer Orders (${data.linked_sales_orders.length})`}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            columns={soColumns}
            dataSource={data.linked_sales_orders}
          />
        </ControlTowerSectionCard>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <ControlTowerSectionCard zone="monthEnd" title={`Work Orders (${data.work_orders.length})`}>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                columns={workOrderColumns}
                dataSource={data.work_orders}
              />
            </ControlTowerSectionCard>
          </Col>
          <Col xs={24} lg={12}>
            <ControlTowerSectionCard zone="overdue" title={`Invoices (${data.invoices.length})`}>
              {data.invoices.some((inv) => inv.is_shared) ? (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={SHARED_INVOICE_SHARE_HINT}
                />
              ) : null}
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                columns={invoiceColumns}
                dataSource={data.invoices}
              />
            </ControlTowerSectionCard>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <ControlTowerSectionCard zone="pendingReview" title="Job Costing (estimation-based budget)">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Budget total">
                  {formatCurrency(data.job_costing.budget_total)}
                </Descriptions.Item>
                <Descriptions.Item label="Posted cost total">
                  {formatCurrency(data.job_costing.posted_cost_total)}
                </Descriptions.Item>
                <Descriptions.Item label="Committed cost">
                  {formatCurrency(data.profitability.committed_cost)}
                </Descriptions.Item>
                <Descriptions.Item label="Variance">
                  <Text type={data.job_costing.variance < 0 ? 'danger' : undefined}>
                    {formatCurrency(data.job_costing.variance)}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Total hours">
                  {data.job_costing.total_hours.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Pending timesheet approvals">
                  {data.job_costing.pending_timesheet_approvals}
                </Descriptions.Item>
              </Descriptions>
            </ControlTowerSectionCard>
          </Col>
          <Col xs={24} lg={12}>
            <ControlTowerSectionCard zone="dueSoon" title="Procurement Snapshot">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Open PR count">{data.procurement.open_pr_count}</Descriptions.Item>
                <Descriptions.Item label="Open PO count">{data.procurement.open_po_count}</Descriptions.Item>
                <Descriptions.Item label="Total PO count">
                  {data.procurement.po_count ?? data.procurement.pos.length}
                </Descriptions.Item>
                <Descriptions.Item label="PO ordered">
                  {formatCurrency(data.procurement.cost_rollup?.po_ordered ?? 0)}
                </Descriptions.Item>
                <Descriptions.Item label="GRN received">
                  {formatCurrency(data.procurement.cost_rollup?.grn_received ?? 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Supplier invoiced">
                  {formatCurrency(data.procurement.cost_rollup?.supplier_invoiced ?? 0)}
                </Descriptions.Item>
                <Descriptions.Item label="Supplier paid">
                  {formatCurrency(data.procurement.cost_rollup?.supplier_paid ?? 0)}
                </Descriptions.Item>
              </Descriptions>
            </ControlTowerSectionCard>
          </Col>
        </Row>

        <EstimationPerformancePanel
          performance={data.performance}
          currencySymbol={
            (() => {
              const sample = formatCurrency(0);
              const m = String(sample).match(/^[^\d]+/);
              return m ? m[0].trim() : 'AED';
            })()
          }
          projectScoped
        />

        <ControlTowerSectionCard
          zone="ordersInProgress"
          title={`Purchase Orders (${data.procurement.po_count ?? data.procurement.pos.length})`}
        >
          <Table
            rowKey="id"
            size="small"
            pagination={
              data.procurement.pos.length > 10
                ? { pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} POs` }
                : false
            }
            columns={procurementColumns}
            dataSource={data.procurement.pos}
            locale={{ emptyText: 'No purchase orders linked to this project' }}
          />
        </ControlTowerSectionCard>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <ControlTowerSectionCard zone="collectionsDetail" title="Quality">
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <ControlTowerSubQueuePanel zone="qmsNcrQueue" title="Open NCRs" count={data.quality.open_ncr_count}>
                  <Text>Critical NCRs: <Text type={data.quality.critical_ncr_count > 0 ? 'danger' : undefined}>{data.quality.critical_ncr_count}</Text></Text>
                </ControlTowerSubQueuePanel>
                <ControlTowerSubQueuePanel zone="qmsCapaOverdue" title="Open CAPAs" count={data.quality.open_capa_count}>
                  <Text>Pending inspections: {data.quality.pending_inspection_count}</Text>
                </ControlTowerSubQueuePanel>
              </Space>
            </ControlTowerSectionCard>
          </Col>
          <Col xs={24} lg={12}>
            <ControlTowerSectionCard zone="documentPrep" title="Estimation + Links">
              <Descriptions column={1} size="small" style={{ marginBottom: 12 }}>
                <Descriptions.Item label="Estimated budget total">
                  {formatCurrency(data.estimation.estimated_budget_total)}
                </Descriptions.Item>
              </Descriptions>
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                columns={boqColumns}
                dataSource={data.estimation.boqs}
              />
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                columns={treColumns}
                dataSource={data.estimation.trading_rental_estimations}
                style={{ marginTop: 12 }}
              />
              <Space direction="vertical">
                {data.estimation.opportunity && (
                  <Button type="link" icon={<CheckCircleOutlined />} onClick={() => navigate(data.estimation.opportunity!.link_path)}>
                    Opportunity: {data.estimation.opportunity.opportunity_number}
                  </Button>
                )}
                {data.estimation.quotation && (
                  <Button type="link" icon={<CheckCircleOutlined />} onClick={() => navigate(data.estimation.quotation!.link_path)}>
                    Quotation: {data.estimation.quotation.quotation_number}
                  </Button>
                )}
                <Button type="link" icon={<WarningOutlined />} onClick={() => navigate('/grc/moc')}>
                  Related MOC: {data.related_mocs.length}
                </Button>
              </Space>
            </ControlTowerSectionCard>
          </Col>
        </Row>
      </Space>
    </WorkspaceLayout>
  );
};

export default ProjectStatusCheckPage;
